import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { isOnboardingFinished } from "../../../Utils/onboarding/isOnboardingFinished"
import { superAdminRoleId } from "../../../Utils/permission"
import {
  acceptAdminInviteFailure,
  acceptAdminInviteSuccess,
  authErrorReset,
  authFetchingReset,
  authSetProp,
  changeAdminPasswordFailure,
  changeAdminPasswordSuccess,
  checkEmailInitSuccess,
  clearState,
  commitAdminSecurity2FAFailure,
  commitAdminSecurity2FASuccess,
  confirm2FASetupFailure,
  confirm2FASetupSuccess,
  confirmAdminSecurity2FAFailure,
  confirmAdminSecurity2FASuccess,
  createAccountFailure,
  createAccountSuccess,
  generate2FASetupFailure,
  generate2FASetupSuccess,
  generateAdminSecurity2FAFailure,
  generateAdminSecurity2FASuccess,
  refreshTokenFailure,
  refreshTokenSuccess,
  sendConfirmEmailFailure,
  sendConfirmEmailSuccess,
  setShowModalPremiumFeatures,
  signInFailure,
  signInSuccess,
  signUpFailure,
  signUpSuccess,
  socialAuthDeleteSuccess,
  socialAuthFailure,
  socialAuthReset,
  socialAuthSuccess,
  updateProfile,
  updateTokens,
  verify2FAFailure,
  verifyTokenFailure,
  verifyTokenSuccess
} from "./auth.actions"
import { AUTH_SLICE_NAME } from "./auth.constants"
import {
  acceptAdminInvite,
  changeAdminPassword,
  changeEmail,
  checkPassword,
  commitAdminSecurity2FA,
  confirm2FASetup,
  confirmAdminSecurity2FA,
  confirmEmail,
  confirmEmailInit,
  confirmPassword,
  createAccount,
  createUserPassword,
  generate2FASetup,
  generateAdminSecurity2FA,
  logoutThunk,
  refreshToken,
  resendVerificationLink,
  sendConfirmEmail,
  shopifyAuth,
  signIn,
  socialAuth,
  socialAuthConnect,
  socialAuthDelete,
  verify2FA,
  verifyToken
} from "./auth.thunks"

const initialState = {
  userInfo: null,
  codeValid: false,
  isPasswordCreated: false,
  accessToken: null,
  refreshToken: null,
  isValidRefreshToken: false,
  error: null,
  fetching: false,

  updateFetching: false,
  updateErrors: false,

  socialAccount: "",

  socialAuthConnectResult: null,
  socialAuthDeleteResult: null,

  isAdmin: false,
  role: null,

  emailSent: false,

  showModalPremiumFeatures: false,
  confirmEmailTimeToNextTry: null,

  rememberMe: null,
  message: null,

  tempEmail: null,
  ephemeralToken: null,
  is2FAConfigured: false,
  twoFactorSecret: null,
  twoFactorQrCode: null,
  twoFactorSetupToken: null,
  twoFactorRecoveryCodes: [],
  twoFactorCommitToken: null
}

const getRoleFields = (userInfo) => ({
  isAdmin:
    userInfo?.role?.id === 2 || userInfo?.role?.id === 5 || userInfo?.role?.id === superAdminRoleId,
  role: userInfo?.role?.id
})

const resetAuthState = (state) => {
  localStorage.removeItem("completely_finished")
  const { error, rememberMe } = state
  Object.assign(state, initialState, { error, rememberMe })
}

const setFetching = (state) => {
  state.fetching = true
  state.error = null
}

const slice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState,
  reducers: {
    setIsPasswordCreated(state, action) {
      state.isPasswordCreated = action.payload
    },
    clear2FASetupState(state) {
      state.twoFactorSecret = null
      state.twoFactorQrCode = null
      state.twoFactorSetupToken = null
      state.twoFactorRecoveryCodes = []
      state.twoFactorCommitToken = null
    },
    setEmailSent(state, action) {
      state.fetching = false
      state.emailSent = action.payload
    },
    logout(state) {
      resetAuthState(state)
    },
    socialAccountSave(state, action) {
      state.socialAccount = action.payload
    },
    socialAuthDeleteFailure(state, action) {
      state.error = action.payload
      state.fetching = false
    },
    socialAuthConnectReset() {
      // No-op: the legacy reducer never wired a handler for this action type either.
    },
    setRemember(state, action) {
      state.rememberMe = action.payload
    },
    createBillingDescriptorSuccess(state) {
      if (!isOnboardingFinished(state.userInfo)) {
        state.userInfo = { ...state.userInfo, next_step: 7 }
      }
    },
    clearAuthMessage(state) {
      state.message = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        const prevErrors = state.error
        state.fetching = true
        state.error = { ...prevErrors }
      })
      .addCase(verify2FA.pending, setFetching)
      .addCase(generate2FASetup.pending, setFetching)
      .addCase(confirm2FASetup.pending, setFetching)
      .addCase(generateAdminSecurity2FA.pending, setFetching)
      .addCase(confirmAdminSecurity2FA.pending, setFetching)
      .addCase(commitAdminSecurity2FA.pending, setFetching)
      .addCase(acceptAdminInvite.pending, setFetching)
      .addCase(createAccount.pending, setFetching)
      .addCase(resendVerificationLink.pending, setFetching)
      .addCase(changeEmail.pending, setFetching)
      .addCase(confirmEmail.pending, setFetching)
      .addCase(confirmEmailInit.pending, setFetching)
      .addCase(sendConfirmEmail.pending, setFetching)
      .addCase(checkPassword.pending, setFetching)
      .addCase(confirmPassword.pending, setFetching)
      .addCase(createUserPassword.pending, setFetching)
      .addCase(changeAdminPassword.pending, setFetching)
      .addCase(shopifyAuth.pending, setFetching)
      .addCase(socialAuth.pending, setFetching)
      .addCase(socialAuthConnect.pending, setFetching)
      .addCase(socialAuthDelete.pending, setFetching)
      .addCase(refreshToken.pending, (state) => {
        state.fetching = true
      })
      .addCase(verifyToken.pending, (state) => {
        state.fetching = true
        state.isValidRefreshToken = false
      })
      .addCase(logoutThunk.pending, resetAuthState)
      .addCase(signInSuccess, (state, action) => {
        const { userInfo, accessToken, refreshToken: token } = action.payload
        Object.assign(state, {
          fetching: false,
          error: null,
          userInfo,
          accessToken,
          refreshToken: token,
          ...getRoleFields(userInfo),
          ephemeralToken: null,
          is2FAConfigured: false,
          twoFactorSecret: null,
          twoFactorQrCode: null,
          twoFactorSetupToken: null,
          twoFactorRecoveryCodes: [],
          twoFactorCommitToken: null
        })
      })
      .addCase(checkEmailInitSuccess, (state, action) => {
        state.fetching = false
        state.error = null
        state.codeValid = action.payload
      })
      .addCase(signInFailure, (state, action) => {
        const prevErrors = state.error
        state.error = { ...prevErrors, ...action.payload }
        state.fetching = false
      })
      .addCase(verify2FAFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(generate2FASetupSuccess, (state, action) => {
        const payload = action.payload
        state.fetching = false
        state.error = null
        state.twoFactorSecret = payload?.secret || null
        state.twoFactorQrCode = payload?.qr_code || null
        state.twoFactorSetupToken = payload?.setup_token || null
        state.twoFactorRecoveryCodes = []
        state.twoFactorCommitToken = null
      })
      .addCase(generate2FASetupFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(confirm2FASetupSuccess, (state, action) => {
        const {
          userInfo,
          accessToken,
          refreshToken: token,
          recoveryCodes,
          commitToken
        } = action.payload
        const nextUserInfo = userInfo || state.userInfo
        Object.assign(state, {
          fetching: false,
          error: null,
          userInfo: nextUserInfo,
          accessToken: accessToken || state.accessToken,
          refreshToken: token || state.refreshToken,
          ...getRoleFields(nextUserInfo),
          ephemeralToken: null,
          is2FAConfigured: true,
          twoFactorSecret: null,
          twoFactorQrCode: null,
          twoFactorSetupToken: null,
          twoFactorRecoveryCodes: recoveryCodes || [],
          twoFactorCommitToken: commitToken || null
        })
      })
      .addCase(confirm2FASetupFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(generateAdminSecurity2FASuccess, (state, action) => {
        const payload = action.payload
        state.fetching = false
        state.error = null
        state.twoFactorSecret = payload?.secret || null
        state.twoFactorQrCode = payload?.qr_code || null
        state.twoFactorSetupToken = payload?.setup_token || null
        state.twoFactorRecoveryCodes = []
        state.twoFactorCommitToken = null
      })
      .addCase(generateAdminSecurity2FAFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(confirmAdminSecurity2FASuccess, (state, action) => {
        const { recoveryCodes, commitToken } = action.payload
        state.fetching = false
        state.error = null
        state.twoFactorSetupToken = null
        state.twoFactorQrCode = null
        state.twoFactorSecret = null
        state.twoFactorRecoveryCodes = recoveryCodes || []
        state.twoFactorCommitToken = commitToken || null
      })
      .addCase(confirmAdminSecurity2FAFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(commitAdminSecurity2FASuccess, (state) => {
        state.fetching = false
        state.error = null
      })
      .addCase(commitAdminSecurity2FAFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(acceptAdminInviteSuccess, (state, action) => {
        state.fetching = false
        state.error = null
        state.ephemeralToken = action.payload || null
      })
      .addCase(acceptAdminInviteFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(createAccountSuccess, (state, action) => {
        const { userInfo, accessToken, refreshToken: token } = action.payload
        Object.assign(state, {
          fetching: false,
          error: null,
          userInfo,
          accessToken,
          refreshToken: token,
          ...getRoleFields(userInfo)
        })
      })
      .addCase(createAccountFailure, (state, action) => {
        const prevErrors = state.error
        state.error = { ...prevErrors, ...action.payload }
        state.fetching = false
      })
      .addCase(authErrorReset, (state) => {
        state.error = null
        state.fetching = false
      })
      .addCase(updateTokens, (state, action) => {
        const { accessToken, refreshToken: token } = action.payload
        state.accessToken = accessToken
        state.refreshToken = token
      })
      .addCase(sendConfirmEmailSuccess, (state, action) => {
        state.fetching = false
        state.error = null
        state.confirmEmailTimeToNextTry = action.payload
      })
      .addCase(sendConfirmEmailFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(clearState, (state) => {
        const { isPasswordCreated } = state
        Object.assign(state, initialState, { isPasswordCreated })
      })
      .addCase(updateProfile, (state, action) => {
        const data = action.payload
        state.userInfo = data?.user
          ? { ...state.userInfo, ...data.user }
          : { ...state.userInfo, ...data }
      })
      .addCase(signUpSuccess, (state, action) => {
        state.fetching = false
        state.error = null
        state.isPasswordCreated = false
        state.message = action.payload
      })
      .addCase(signUpFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(changeAdminPasswordSuccess, (state, action) => {
        state.fetching = false
        state.error = null
        state.message = action.payload
      })
      .addCase(changeAdminPasswordFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(socialAuthFailure, (state, action) => {
        state.error = action.payload
        state.fetching = false
      })
      .addCase(socialAuthSuccess, (state, action) => {
        const { userInfo, accessToken, refreshToken: token } = action.payload
        Object.assign(state, {
          fetching: false,
          error: null,
          ...(isOnboardingFinished(userInfo) && { socialAuthConnectResult: true }),
          userInfo,
          accessToken,
          refreshToken: token,
          ...getRoleFields(userInfo)
        })
      })
      .addCase(socialAuthReset, (state) => {
        state.fetching = false
        state.error = null
        state.socialAuthConnectResult = null
        state.socialAuthDeleteResult = null
        state.socialAccount = ""
      })
      .addCase(socialAuthDeleteSuccess, (state, action) => {
        const userInfo = action.payload
        Object.assign(state, {
          fetching: false,
          error: null,
          socialAuthDeleteResult: true,
          userInfo,
          ...getRoleFields(userInfo)
        })
      })
      .addCase(authFetchingReset, (state) => {
        state.fetching = false
        state.updateFetching = false
      })
      .addCase(setShowModalPremiumFeatures, (state, action) => {
        state.showModalPremiumFeatures = action.payload
      })
      .addCase(authSetProp, (state, action) => {
        const { key, value } = action.payload
        state[key] = value
      })
      .addCase(refreshTokenSuccess, (state, action) => {
        const { access, refresh, user } = action.payload || {}
        state.fetching = false
        state.accessToken = access
        state.refreshToken = refresh
        state.userInfo = user
      })
      .addCase(refreshTokenFailure, (state, action) => {
        state.fetching = false
        state.error = action.payload?.errors
      })
      .addCase(verifyTokenSuccess, (state) => {
        state.fetching = false
        state.isValidRefreshToken = true
      })
      .addCase(verifyTokenFailure, (state, action) => {
        state.fetching = false
        state.isValidRefreshToken = false
        state.error = action.payload?.errors
      })
  }
})

export const authActions = slice.actions

export const AuthSelectors = {
  getUserInfo: (state) => state[AUTH_SLICE_NAME].userInfo,
  getAccessToken: (state) => state[AUTH_SLICE_NAME].accessToken,
  getRefreshToken: (state) => state[AUTH_SLICE_NAME].refreshToken
}

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "authThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
